import { LitElement, html, css } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';

import { showNotification } from '../../LanghoutBetonPortal';
import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import '../../elements/langhout-button/langhout-button';
import '../../elements/search-dropdown/search-dropdown';
import { SearchDropdown } from '../../elements/search-dropdown/search-dropdown';
import '../../elements/langhout-button/langhout-button';
import '../../elements/langhout-checkbox/langhout-checkbox';
import { LanghoutCheckbox } from '../../elements/langhout-checkbox/langhout-checkbox';
import {
  formatDateInput,
  ProductStockTypeOnRequest,
  setBodyStyleObject,
  StockStatusInStock,
  StockStatusLimited,
  StockStatusOutOfStock,
} from '../../util/util';
import { repeat } from 'lit/directives/repeat.js';
import {
  Product,
  ProdEdit,
  ProductExact,
  ProductStockView,
  DeliveryOverTime,
  StyleObject,
  RetourOrderCreateRequest,
  RetourOrderProductCreateRequest,
  RetourOrderDocument,
} from '../../types/types';
import { minusIcon, plusIcon } from '../../elements/icons/icons';
import { sharedStyles } from '../../elements/shared-styles';

type CustomerCodeByNameResponse = {
  customer_code: string;
  error_message: string;
};

@customElement('langhout-beton-create-retour-order')
export class createRetourOrder extends LitElement {
  private readonly styleObject: StyleObject = {
    '--wrapper-width': '1000px',
    '--row-width': '600px',
    '--label-width': '350px',
    '--margin-bottom': '25px',
    '--svg-width': '30px',
    '--amount-column-width': '100px',
    '--amount-margin-right': '20px',
    '--amount-unit-column-width': '140px',
  };

  static styles = [
    sharedStyles,
    css`
      .outer-wrapper {
        padding-bottom: 200px;
      }

      .row {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: center;
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .create-wrapper {
        padding-bottom: 40px;
        width: 100%;
        background-color: var(--primary-color);
        color: var(--text-color-light);
        border-radius: 10px;
        max-width: var(--wrapper-width);
      }

      .label {
        text-align: left;
        width: var(--label-width);
        font-weight: bold;
        margin-bottom: var(--margin-bottom);
      }

      .input-column {
        margin-bottom: var(--margin-bottom);
        width: calc(var(--row-width) - var(--label-width));
      }

      .input-column-prod-amount {
        text-align: left;
        width: calc(var(--amount-column-width) - var(--amount-margin-right));
        max-width: calc(
          var(--amount-column-width) - var(--amount-margin-right)
        );
        margin-right: var(--amount-margin-right);
      }

      .label-column-prod-amount {
        width: var(--amount-column-width);
        text-align: left;
        margin-bottom: var(--margin-bottom);
      }

      .label-column-amount-unit {
        width: var(--amount-unit-column-width);
        text-align: left;
      }

      .label-column-amount-type {
        width: calc(
          var(--row-width) - var(--amount-column-width) - var(
              --amount-unit-column-width
            )
        );
        text-align: left;
      }

      .input-column-prod {
        margin-bottom: var(--margin-bottom);
        width: calc(
          var(--row-width) - var(--amount-column-width) - var(
              --amount-unit-column-width
            )
        );
        max-width: calc(
          var(--row-width) - var(--amount-column-width) - var(
              --amount-unit-column-width
            )
        );
      }

      .checkbox-wrapper {
        margin-bottom: var(--margin-bottom);
      }

      .amount-unit {
        width: var(--amount-unit-column-width);
      }

      .fill-block-products {
        width: calc(var(--row-width) - var(--label-width) - var(--svg-width));
      }

      .fill-block-checkbox {
        width: 205px;
      }

      .checkbox-label {
        text-align: left;
        width: var(--label-width);
      }

      @media (max-width: 779px) {
        .input-column {
          margin-bottom: var(--margin-bottom);
          width: 250px;
          max-width: 250px;
        }

        search-dropdown {
          width: 250px !important;
        }

        .fill-block,
        .fill-block-checkbox,
        .fill-block-products {
          width: 0px;
        }

        .product-label {
          width: 220px;
        }

        .remove-prod {
          right: 0;
        }
      }

      @media (max-width: 925px) {
        .remove-prod {
          right: 0px !important;
        }
        .dot {
          right: 35px !important;
        }
      }

      search-dropdown {
        width: 190px;
      }

      langhout-button {
        width: 150px;
        margin-right: 30px;
      }

      .prod-row {
        position: relative;
      }

      .remove-prod {
        position: absolute;
        top: 2px;
        right: 50px;
      }

      langhout-checkbox {
        margin-left: 15px;
      }

      :host([disableButtons]) {
        pointer-events: none;
        opacity: 0.7;
      }

      .dot {
        position: absolute;
        right: 100px;
        border: 1px solid white;
        background-color: white;
        height: 25px;
        width: 25px;
        min-width: 25px;
        border-radius: 50%;
        display: inline-block;
        margin-right: 10px;
        margin-left: 10px;
        margin-top: 5.5px;
      }

      .dot-red {
        background-color: red;
      }

      .dot-orange {
        background-color: orange;
      }

      .dot-green {
        background-color: var(--primary-color);
      }

      .weight-display {
        width: calc(var(--row-width) - var(--label-width));
        text-align: right;
      }

      .file-list {
        list-style: none;
        padding: 0;
        margin: 5px 0 0 0;
        text-align: left;
      }

      .file-list li {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 4px;
      }

      .file-list li span {
        cursor: pointer;
        color: red;
        font-weight: bold;
      }
    `,
  ];

  @query('#orderID')
  public orderID?: HTMLInputElement;

  @query('#shippingID')
  public shippingID?: HTMLInputElement;

  @query('#retourDate')
  public retourDate?: HTMLInputElement;

  @query('#licensePlate')
  public licensePlate?: HTMLInputElement;

  @query('#transporter')
  public transporter?: HTMLInputElement;

  @query('#customerName')
  public customerName?: HTMLInputElement;

  @query('#comment')
  public comment?: HTMLInputElement;

  @query('#packageReceiptNote')
  public packageReceiptNote?: HTMLInputElement;

  @query('#checkDirectlyCheckbox')
  public checkDirectlyCheckbox?: LanghoutCheckbox;

  @query('#fileInput')
  private fileInput?: HTMLInputElement;

  @property({ type: Array })
  private documents: RetourOrderDocument[] = [];

  @property({ type: Array })
  private documentNames: string[] = [];

  private customer_code = '';

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  constructor() {
    super();
    setBodyStyleObject(this.styleObject);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  @property({ type: Array, attribute: true })
  public prods: ProdEdit[] = [
    {
      amount: 0.0,
      index: 0,
      stock_status: StockStatusInStock,
    },
  ];

  public prodIndex = 0;

  @property({ type: Number, attribute: true })
  private totalWeight = 0;

  private exactProducts: ProductExact[] = [];

  render() {
    return html`
      <div class="row outer-wrapper">
        <div class="create-wrapper column">
          <h3>Retour invoeren</h3>
          ${this.renderNormalInputs()}
          <div class="row">
            <div class="label product-label">Producten</div>
            <div class="fill-block-products"></div>
            <div class="plus-icon" @click="${this.addProdInput}">
              ${plusIcon()}
            </div>
          </div>
          <div class="row">
            <div class="label-column-prod-amount">Aantal</div>
            <div class="label-column-amount-unit">Verk. Eenh.</div>
            <div class="label-column-amount-type">Type</div>
          </div>
          ${repeat(
            this.prods,
            prod => prod.index,
            (item, index) => {
              return html`
                <div class="row prod-row">
                  <div class="input-column-prod-amount">
                    <input
                      value="${item.amount ? item.amount : ''}"
                      placeholder="aantal"
                      type="number"
                      min="1"
                      step="1"
                      class="product-amount"
                      name="product amount"
                      @input=${() => {
                        this.checkStockStatus();
                        this.calculateTotalWeight();
                      }}
                    />
                  </div>
                  ${this.renderAmountInUnits(index)}
                  <div class="fill-block"></div>
                  <div class="input-column-prod">
                    <search-dropdown
                      .selected="${item.prod}"
                      class="search-dropdown"
                      @change="${() => {
                        this.checkStockStatus();
                        this.calculateTotalWeight();
                      }}"
                    ></search-dropdown>
                  </div>
                  ${this.renderStockStatus(item.stock_status)}
                  <div
                    class="remove-prod"
                    @click="${() => {
                      this.removeProdInput(index);
                      setTimeout(() => {
                        this.checkStockStatus();
                        this.calculateTotalWeight();
                      }, 10);
                    }}"
                  >
                    ${minusIcon()}
                  </div>
                </div>
              `;
            },
          )}
          <div class="row">
            <div class="label product-label">Totaal gewicht</div>
            <div class="weight-display">
              ${this.totalWeight.toLocaleString('nl-NL')} kg
            </div>
          </div>
          <div class="row checkbox-wrapper">
            <div class="checkbox-label">Documenten</div>
            <div class="input-column">
              <input
                type="file"
                id="fileInput"
                accept=".png,.jpg,.jpeg,.pdf"
                multiple
                @change="${this.handleFileSelect}"
                style="display:none"
              />
              <langhout-button
                reverse
                text="Bestanden kiezen"
                @click="${() => this.fileInput?.click()}"
              ></langhout-button>
              ${
                this.documentNames.length > 0
                  ? html`<ul class="file-list">
                      ${this.documentNames.map(
                        (name, i) => html`
                          <li>
                            ${name}
                            <span @click="${() => this.removeDocument(i)}"
                              >&times;</span
                            >
                          </li>
                        `,
                      )}
                    </ul>`
                  : ''
              }
            </div>
          </div>
          <div class="row checkbox-wrapper">
            <div class="checkbox-label">Direct controleren</div>
            <langhout-checkbox
              reverse
              id="checkDirectlyCheckbox"
            ></langhout-checkbox>
            <div class="fill-block-checkbox"></div>
          </div>
          <div class="row">
            <div class="fill"></div>
            <langhout-button
              reverse
              text="Afronden"
              @click="${this.createRetourOrder}"
            ></langhout-button>
          </div>
        </div>
      </div>
    `;
  }

  renderAmountInUnits = (index: number) => {
    if (!this.shadowRoot) {
      return html`<div class="amount-unit">-</div>`;
    }

    const productSelects = this.shadowRoot.querySelectorAll('.search-dropdown');
    const prodAmountInputs =
      this.shadowRoot.querySelectorAll('.product-amount');
    const searchDropDown = <SearchDropdown>productSelects[index];
    const amountInputElement = <HTMLInputElement>prodAmountInputs[index];

    if (
      !searchDropDown ||
      !searchDropDown.selected ||
      !amountInputElement ||
      !amountInputElement.value
    ) {
      return html`<div class="amount-unit">-</div>`;
    }
    const amountValue = parseFloat(amountInputElement.value);
    const selected = searchDropDown.selected;
    return html`<div class="amount-unit">
      ${Math.round((amountValue / selected.pieces_per_unit) * 1000) / 1000}
      ${selected.unit}
    </div>`;
  };

  renderStockStatus = (stockStatus: number) => {
    if (stockStatus === StockStatusInStock) {
      return html`<div class="dot dot-green"></div>`;
    }
    if (stockStatus === StockStatusOutOfStock) {
      return html`<div class="dot dot-red"></div>`;
    }
    if (stockStatus === StockStatusLimited) {
      return html`<div class="dot dot-orange"></div>`;
    }
    return html`<div class="dot dot-green"></div>`;
  };

  checkStockStatus = async () => {
    if (!this.shadowRoot) {
      return;
    }

    const productSelects = this.shadowRoot.querySelectorAll('.search-dropdown');
    const prodAmountInputs =
      this.shadowRoot.querySelectorAll('.product-amount');
    productSelects.forEach((e: Element, index: number) => {
      if (!this.retourDate) {
        return;
      }

      const searchDropDown = <SearchDropdown>productSelects[index];
      const amountInputElement = <HTMLInputElement>prodAmountInputs[index];

      const amountValue = parseFloat(amountInputElement.value);
      const selected = searchDropDown.selected;

      if (
        !selected ||
        selected.id === undefined ||
        selected.id === '00000000-0000-0000-0000-000000000000'
      ) {
        return;
      }

      const retourDate = new Date(this.retourDate.value);
      if (selected && amountValue > 0) {
        this.checkStockStatusForIndex(index, retourDate, amountValue, selected);
      }
    });
  };

  checkStockStatusForIndex = async (
    index: number,
    retourDate: Date,
    amountValue: number,
    selected: Product,
  ) => {
    if (selected && amountValue > 0) {
      const productStockView = await this.getProductStock(selected.id);
      if (productStockView.stock_type == ProductStockTypeOnRequest) {
        return;
      }
      const toDeliver = amountValue / selected.pieces_per_unit;
      let stockAmountAtPickup = productStockView.stock_amount;
      if (productStockView.delivery_over_time) {
        productStockView.delivery_over_time.forEach(
          (delivery: DeliveryOverTime) => {
            const deliveryDate = new Date(delivery.date);
            if (deliveryDate <= retourDate) {
              stockAmountAtPickup -= delivery.amount_in_units;
            }
          },
        );
      }
      if (productStockView.planned_production_over_time) {
        productStockView.planned_production_over_time.forEach(
          (delivery: DeliveryOverTime) => {
            const deliveryDate = new Date(delivery.date);
            if (deliveryDate <= retourDate) {
              stockAmountAtPickup += delivery.amount;
            }
          },
        );
      }

      const valueWithNewPickup = stockAmountAtPickup - toDeliver;
      let stockStatus = StockStatusInStock;
      if (productStockView.not_in_stock_amount > valueWithNewPickup) {
        stockStatus = StockStatusOutOfStock;
      } else if (productStockView.limited_stock_amount > valueWithNewPickup) {
        stockStatus = StockStatusLimited;
      }
      const temp = this.prods;
      temp[index].stock_status = stockStatus;
      this.prods = [];
      this.prods = temp;
    }
  };

  renderNormalInputs = () => {
    return html` <div class="row">
        <div class="label">Ordernummer LH</div>
        <div class="input-column">
          <input
            type="number"
            min="1"
            step="1"
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
            placeholder="ordernummer"
            id="orderID"
            name="orderID"
            @input=${() => {
              const orderNumber = this.orderID
                ? parseInt(this.orderID.value)
                : NaN;
              if (orderNumber.toString().length >= 6) {
                this.retrieveOrderInfoExact(orderNumber.toString());
              }
            }}
          />
        </div>
      </div>
      <div class="row">
        <div class="label">Vracht ID</div>
        <div class="input-column">
          <input
            type="text"
            placeholder="vracht id"
            id="shippingID"
            name="shippingID"
          />
        </div>
      </div>
      <div class="row">
        <div class="label">Klantnaam</div>
        <div class="input-column">
          <input
            type="text"
            placeholder="klantnaam"
            id="customerName"
            name="customerName"
          />
        </div>
      </div>
      <div class="row">
        <div class="label">Kenteken</div>
        <div class="input-column">
          <input
            type="text"
            placeholder="kenteken"
            id="licensePlate"
            name="licensePlate"
          />
        </div>
      </div>
      <div class="row">
        <div class="label">Transporteur</div>
        <div class="input-column">
          <input
            type="text"
            placeholder="transporteur"
            id="transporter"
            name="transporter"
          />
        </div>
      </div>
      <div class="row">
        <div class="label">Opmerking</div>
        <div class="input-column">
          <input
            type="text"
            placeholder="opmerking"
            id="comment"
            name="comment"
          />
        </div>
      </div>
      <div class="row">
        <div class="label">Retourdatum</div>
        <div class="input-column">
          <input
            type="date"
            id="retourDate"
            name="retourDate"
            value="${formatDateInput(new Date().toISOString())}"
            @input=${() => {
              this.checkStockStatus();
            }}
          />
        </div>
      </div>`;
  };

  calculateTotalWeight = () => {
    setTimeout(() => {
      if (!this.shadowRoot) {
        return;
      }
      const productSelects =
        this.shadowRoot.querySelectorAll('.search-dropdown');
      const prodAmountInputs =
        this.shadowRoot.querySelectorAll('.product-amount');
      const result: ProdEdit[] = [];
      productSelects.forEach((e: Element, index: number) => {
        const searchDropDown = <SearchDropdown>e;
        const amountInputElement = <HTMLInputElement>prodAmountInputs[index];
        result.push(<ProdEdit>{
          amount: parseFloat(amountInputElement.value),
          prod: searchDropDown.selected ? searchDropDown.selected : undefined,
        });
      });
      let totalWeight = 0;
      result.forEach((p: ProdEdit) => {
        if (!p.prod || isNaN(p.amount)) {
          return;
        }
        totalWeight += p.amount * p.prod.weight;
      });
      totalWeight = Math.round(totalWeight);

      this.totalWeight = totalWeight;
    }, 5);
  };

  addProdInput = () => {
    const temp = this.prods;
    this.prodIndex++;
    temp.push({
      amount: 0.0,
      index: this.prodIndex,
      stock_status: StockStatusInStock,
    });
    this.prods = [];
    this.prods = temp;
  };

  removeProdInput = (index: number) => {
    if (!this.shadowRoot) {
      return;
    }
    const productSelects = this.shadowRoot.querySelectorAll('.search-dropdown');
    const prodAmountInputs =
      this.shadowRoot.querySelectorAll('.product-amount');
    const newProds: ProdEdit[] = [];
    prodAmountInputs.forEach((e: Element, otherIndex: number) => {
      if (otherIndex !== index) {
        const inputElement = <HTMLInputElement>e;
        const searchDropDown = <SearchDropdown>productSelects[otherIndex];

        newProds.push({
          amount: parseFloat(inputElement.value),
          index: this.prods[otherIndex].index,
          prod: searchDropDown.selected,
          stock_status: StockStatusInStock,
        });
      }
    });
    this.prods = [];
    this.prods = newProds;
  };

  createRetourOrder = async () => {
    if (this.disableButtons) return;
    this.disableButtons = true;
    await this.executeRetourOrderCreateCall();
    this.disableButtons = false;
  };

  newRetourOrderCreateRequest = (): RetourOrderCreateRequest => {
    const productSelects = this.getProducts();

    return <RetourOrderCreateRequest>{
      order_id: this.orderID ? parseInt(this.orderID.value) : NaN,
      shipping_id: this.shippingID ? this.shippingID.value : '',
      customer_name: this.customerName ? this.customerName.value : '',
      customer_code: this.customer_code,
      license_plate: this.licensePlate ? this.licensePlate.value : '',
      transporter: this.transporter ? this.transporter.value : '',
      retour_date: this.retourDate
        ? new Date(this.retourDate.value).toISOString()
        : '',
      check_directly: this.checkDirectlyCheckbox
        ? this.checkDirectlyCheckbox.checked
        : false,
      comment: this.comment ? this.comment.value : '',
      products: productSelects,
      documents: this.documents,
    };
  };

  getCustomerCodeByName = async (
    name: string,
  ): Promise<CustomerCodeByNameResponse> => {
    const resp = await authenticatedAPICall(
      `/accounts/customers/${name}`,
      'GET',
      new Headers(),
    );
    const response: CustomerCodeByNameResponse = {
      customer_code: '',
      error_message: '',
    };
    if (isSuccessfulAPIResponse(resp)) {
      response.customer_code = resp.body.customer_code;
      return response;
    }
    if (resp.error_message) {
      response.error_message = resp.error_message;
    }
    return response;
  };

  executeRetourOrderCreateCall = async () => {
    const retourOrder: RetourOrderCreateRequest =
      this.newRetourOrderCreateRequest();
    const errMsg = await this.validateRetourOrder(retourOrder);
    if (errMsg !== '') {
      showNotification(errMsg);
      return;
    }
    await this.callCreate(retourOrder);
  };

  callCreate = async (retourOrder: RetourOrderCreateRequest) => {
    const resp = await authenticatedAPICall(
      '/retour-orders',
      'POST',
      new Headers(),
      JSON.stringify(retourOrder),
    );
    if (isSuccessfulAPIResponse(resp) && resp.body.success) {
      window.location.href = '/retour-orders';
    }
  };

  retrieveOrderInfoExact = async (orderNumber: string) => {
    if (orderNumber === '') {
      return;
    }
    this.searchOrderHeaderExact(orderNumber.toString());
  };

  searchOrderHeaderExact = async (orderNumber: string) => {
    const path = `/exact-globe/sales-order-header/${orderNumber}`;
    const result = await authenticatedAPICall(path, 'GET', new Headers());

    if (isSuccessfulAPIResponse(result)) {
      if (this.customerName) {
        this.customerName.value = result.body.order_debtor_name;
        this.customerName.disabled = true;
      }

      this.customer_code = result.body.order_debtor_code;
    }
  };

  validateRetourOrder = async (
    retourOrder: RetourOrderCreateRequest,
  ): Promise<string> => {
    for (let i = 0; i < retourOrder.products.length; i++) {
      const p = retourOrder.products[i];
      if (p.amount === 0 || isNaN(p.amount)) {
        return 'Product with amount "0" is not valid';
      }
      if (!p.id || p.id === '') {
        return 'Please select the product type';
      }
    }

    return '';
  };

  getProducts = (): RetourOrderProductCreateRequest[] => {
    if (!this.shadowRoot) {
      return [];
    }
    const productSelects = this.shadowRoot.querySelectorAll('.search-dropdown');
    const prodAmountInputs =
      this.shadowRoot.querySelectorAll('.product-amount');
    const result: RetourOrderProductCreateRequest[] = [];
    productSelects.forEach((e: Element, index: number) => {
      const searchDropDown = <SearchDropdown>e;
      const amountInputElement = <HTMLInputElement>prodAmountInputs[index];
      result.push({
        amount: parseFloat(amountInputElement.value),
        id: searchDropDown.selected ? searchDropDown.selected.id : '',
      });
    });
    return result;
  };

  handleFileSelect = async (e: Event) => {
    const input = e.target as HTMLInputElement;
    if (!input.files) return;
    const newDocs: RetourOrderDocument[] = [];
    const newNames: string[] = [];
    for (const file of Array.from(input.files)) {
      const base64 = await this.fileToBase64(file);
      newDocs.push({ type: file.type, document: base64 });
      newNames.push(file.name);
    }
    this.documents = [...this.documents, ...newDocs];
    this.documentNames = [...this.documentNames, ...newNames];
    input.value = '';
  };

  removeDocument = (index: number) => {
    this.documents = this.documents.filter((_, i) => i !== index);
    this.documentNames = this.documentNames.filter((_, i) => i !== index);
  };

  private fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  getProductStock = async (id: string): Promise<ProductStockView> => {
    const result = await authenticatedAPICall(
      `/stock/products/${id}`,
      'GET',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      return result.body;
    }
    return <ProductStockView>{};
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-create-retour-order': createRetourOrder;
  }
}
